import * as React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { AttributeDetails } from '@/elements/shared/AttributeDetails/AttributeDetails';
import { IAttributeTextData } from '@canvas-logic/clp-domain';
import classNames from 'classnames';
import { AttributeStateBase } from '@/elements/base/AttributeStateBase';
import { useAttributeTranslation } from '@/localization/useAttributeTranslation';
import { useContainerLevel } from '@/ui-kit/contexts/ContainerLevelContext';
import { gapLevelToCssValue } from '@/elements/shared/gapUtils';

interface Props {
  state: AttributeStateBase<any>;
  textData?: IAttributeTextData;
  children?: React.ReactNode;
}

export const AttributeCfgUIElement = observer(function AttributeCfgUIElement({
  state,
  textData: textDataProp,
  children,
}: Props) {
  const textData = textDataProp ?? state.textData;
  const { t: _tTranslation } = useTranslation('translation');
  const { uiControl } = state.viewElement;
  const selectedTextData = state.selectedTextData;
  const containerLevel = useContainerLevel();
  const tAttr = useAttributeTranslation(state.componentKey, state.attributeKey);
  if (!state.visible) {
    return null;
  }

  const title = uiControl.showTitle ? tAttr.name(textData.name) : undefined;
  const description = uiControl.showDescription ? textData.description : undefined;
  const localizedSelectedTextData = selectedTextData
    ? {
        ...selectedTextData,
        label:
          selectedTextData.label === undefined
            ? selectedTextData.label
            : tAttr.value(selectedTextData.value, selectedTextData.label),
      }
    : selectedTextData;

  const cardsGapCss = gapLevelToCssValue(uiControl.cardsGap);

  return (
    <div
      className={classNames('cp-attribute', 'cp-attribute--' + state.viewElement.type)}
      data-element-id={state.viewElement.id}
      data-component-key={state.componentKey}
      data-attribute-key={state.attributeKey}
      style={cardsGapCss ? ({ '--cards-gap': cardsGapCss } as React.CSSProperties) : undefined}
    >
      {(title || description) && (
        <div className={classNames('cp-attribute__header')} data-title-level={containerLevel}>
          {title && <span className="cp-attribute__header-title">{title}</span>}
          {description && <span className="cp-attribute__header-description">{description}</span>}
        </div>
      )}
      {children}
      {uiControl.showDetails && <AttributeDetails uiControl={uiControl} selectedTextData={localizedSelectedTextData} />}
    </div>
  );
});
